import type {
  Resource,
  Patient,
  Condition,
  Medication,
  MedicationRequest,
  Observation,
  AllergyIntolerance,
  PlanDefinition,
  Task,
  DocumentReference,
  ServiceRequest,
  CarePlan,
  CareTeam,
  Practitioner,
  Flag,
  Communication,
  Provenance,
  Contract,
  QuestionnaireResponse,
  Extension,
  // Aliased because the unqualified name `Location` resolves to the DOM's
  // global `Location` interface.
  Location as FhirLocation,
} from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import moment from 'moment';

import type {
  FlatAlert,
  FlatAlertPatient,
  FlatAllergy,
  FlatCarePlan,
  FlatCareTeam,
  FlatCommunication,
  FlatCondition,
  FlatContract,
  FlatFlag,
  FlatLab,
  FlatLocation,
  FlatMedication,
  FlatNote,
  FlatPlan,
  FlatPractitioner,
  FlatPreference,
  FlatProvenance,
  FlatQuestionnaireResponse,
  FlatReferral,
  FlatServiceRequest,
  FlatTask,
  FlatVital,
} from '../types/flat-fhir';
import type { FlatPatient } from '../types/patient-data';

/**
 * Reads a patient extension value by `valueCoding.code` (or nested / CodeableConcept variants).
 *
 * @param fhirPatient - Raw FHIR Patient resource.
 * @param code - Extension coding code to match (e.g. `REFERRAL_PARTNER`).
 * @returns The coding display, or the code when display is absent.
 */
export function getPatientExtensionCodingDisplay(
  fhirPatient: Patient | undefined,
  code: string
): string | undefined {
  if (!fhirPatient || !code) {
    return undefined;
  }

  const normalizedCode = code.toUpperCase();
  const extensions = fhirPatient.extension;
  const extensionList: Extension[] = Array.isArray(extensions)
    ? extensions
    : extensions
      ? [extensions]
      : [];

  const matchCoding = (
    coding: { code?: string | undefined; display?: string | undefined } | undefined,
  ): string | undefined => {
    if (!coding?.code || coding.code.toUpperCase() !== normalizedCode) {
      return undefined;
    }
    const display = coding.display?.trim();
    return display || coding.code;
  };

  for (const ext of extensionList) {
    const fromValueCoding = matchCoding(ext?.valueCoding);
    if (fromValueCoding) {
      return fromValueCoding;
    }

    const fromConcept = ext?.valueCodeableConcept?.coding
      ?.map((coding) => matchCoding(coding))
      .find(Boolean);
    if (fromConcept) {
      return fromConcept;
    }

    if (ext?.extension?.length) {
      const nested = getPatientExtensionCodingDisplay({ resourceType: 'Patient', extension: ext.extension }, code);
      if (nested) {
        return nested;
      }
    }
  }

  const fhirPathResults = [
    fhirpath.evaluate(
      fhirPatient,
      `extension.where(valueCoding.code='${code}').valueCoding.display`
    ),
    fhirpath.evaluate(
      fhirPatient,
      `extension.where(valueCodeableConcept.coding.code='${code}').valueCodeableConcept.coding.display`
    ),
    fhirpath.evaluate(
      fhirPatient,
      `extension.extension.where(valueCoding.code='${code}').valueCoding.display`
    ),
  ];

  for (const result of fhirPathResults) {
    const value = Array.isArray(result) ? result[0] : result;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

export function parseFHIRPractitioner(fhirPractitioner: Practitioner): FlatPractitioner {
  const lastName = fhirpath.evaluate(fhirPractitioner, 'Practitioner.name.family').toString();
  const firstName = fhirpath.evaluate(fhirPractitioner, 'Practitioner.name.given').toString();

  const flatPractitioner: FlatPractitioner = {
    id: fhirpath.evaluate(fhirPractitioner, 'Practitioner.id').toString(),

    gender: fhirpath.evaluate(fhirPractitioner, 'Practitioner.gender').toString(),

    lastName,

    firstName,

    fullName: firstName + ' ' + lastName,

    email: fhirpath.evaluate(fhirPractitioner, "Practitioner.telecom.where(system='email').value")[0],

    type: fhirpath
      .evaluate(fhirPractitioner, "identifier.where(system='https://projectwell.io/fhir/practitioner-types').value")
      .toString(),
  };

  return flatPractitioner;
}

export function parseFHIRTask(fhirTask: Task): FlatTask {
  const flatTask: FlatTask = {
    id: fhirpath.evaluate(fhirTask, 'Task.id').toString(),
    description: fhirpath.evaluate(fhirTask, 'Task.description').toString(),
    priority: fhirpath.evaluate(fhirTask, 'Task.priority').toString(),
    reasonCode: fhirpath.evaluate(fhirTask, 'Task.reasonCode.text').toString(),
    startDate: fhirpath.evaluate(fhirTask, 'Task.executionPeriod.start').toString(),
    dueDate: fhirpath.evaluate(fhirTask, 'Task.executionPeriod.end').toString(),
    status: fhirpath.evaluate(fhirTask, 'Task.status').toString(),
    note: fhirpath.evaluate(fhirTask, 'Task.note'),
    code: fhirpath.evaluate(fhirTask, 'Task.code.coding.first().code').toString(),
    programFoodBudget: fhirpath.evaluate(
      fhirTask,
      "extension.where(url='https://projectwell.io/fhir/extensions/program-food-budget').valueDecimal"
    )?.[0],
    lastModifiedBy: fhirpath
      .evaluate(fhirTask, "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value")
      .toString(),
    referralId: fhirpath
      .evaluate(fhirTask, "groupIdentifier.where(system='https://projectwell.io/fhir/identifiers/referral-id').value")
      .toString(),
    lastUpdated: fhirpath.evaluate(fhirTask, 'meta.lastUpdated').toString(),
  };

  return flatTask;
}

export function parseFHIRTaskReasonCode(fhirTask: Task): string {
  return fhirpath.evaluate(fhirTask, 'Task.reasonCode.text').toString();
}

export function parseFHIRPlan(fhirPlan: PlanDefinition): FlatPlan {
  const flatPlan: FlatPlan = {

    def: fhirpath.evaluate(fhirPlan, 'PlanDefinition.name').toString(),
  };

  return flatPlan;
}

export function parseFHIRAllergy(fhirAllergy: AllergyIntolerance): FlatAllergy {
  const flatAllergy: FlatAllergy = {

    category: fhirpath
    .evaluate(fhirAllergy, 'AllergyIntolerance.category.first().first().toString()')
    .toString(),
    recordedDate: fhirpath.evaluate(fhirAllergy, 'AllergyIntolerance.recordedDate').toString(),
    note: fhirpath.evaluate(fhirAllergy, 'AllergyIntolerance.note.first().text').toString(),
    substance: fhirpath
    .evaluate(fhirAllergy, 'AllergyIntolerance.reaction.substance.coding.first().display')
    .toString(),
    element: fhirpath.evaluate(fhirAllergy, 'AllergyIntolerance.code.coding.display').toString(),
    description: fhirpath.evaluate(fhirAllergy, 'AllergyIntolerance.reaction.description').toString(),
    criticality: fhirpath.evaluate(fhirAllergy, 'AllergyIntolerance.criticality').toString(),
  };

  return flatAllergy;
}

/**
 * Reads the `value` property off a FHIRPath result element.
 *
 * `evaluate` yields `unknown[]`; this narrows one element to the object shape the
 * caller expects. A non-object element simply has no `value` and yields undefined.
 */
function readValueProperty(element: unknown): unknown {
  if (typeof element === 'object' && element !== null && 'value' in element) {
    const { value } = element;
    return value;
  }
  return undefined;
}

/**
 * Stringifies a FHIRPath result element.
 *
 * A null/undefined element raises a TypeError; that throw is pinned behaviour.
 */
function readStringified(element: unknown): string {
  if (element === null || element === undefined) {
    throw new TypeError('Cannot read properties of ' + String(element) + " (reading 'toString')");
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(element);
}

export function parseFHIRContract(fhirContract: Contract): FlatContract {
  // Each `quantity` element is a FHIR Quantity; only its `value` is read.
  const maxMealEquivalentsQuantity = fhirpath.evaluate(
    fhirContract,
    "term.asset.valuedItem.where(identifier.value='MAX-ME').quantity"
  )[0];

  const maxBudgetQuantity = fhirpath.evaluate(
    fhirContract,
    "term.asset.valuedItem.where(identifier.value='MAX-COST').quantity"
  )[0];

  const flatContract: FlatContract = {
    id: fhirContract.id,
    name: fhirContract.name,
    status: fhirContract.status,
    maxMealEquivalents: readValueProperty(maxMealEquivalentsQuantity),
    maxBudget: readValueProperty(maxBudgetQuantity),
  };

  return flatContract;
}

export function parseFHIRPatient(fhirPatient: Patient): FlatPatient {
  const org: string = fhirpath.evaluate(fhirPatient, 'managingOrganization.reference').toString();

  const firstName: unknown[] = fhirpath.evaluate(fhirPatient, 'name.given.first()');
  const lastName: unknown[] = fhirpath.evaluate(fhirPatient, 'name.family');
  const dob = moment(fhirPatient.birthDate).format('MM/DD/YYYY');

  const providerDisplay = getPatientExtensionCodingDisplay(fhirPatient, 'PCP_ORGANIZATION');

  const flatPatient: FlatPatient = {
    id: fhirPatient.id,
    lastUpdated: fhirpath.evaluate(fhirPatient, 'meta.lastUpdated').toString(),
    org: org.substring(org.indexOf('/') + 1),
    firstName,
    middleName:
      fhirpath.evaluate(fhirPatient, 'name.given')?.length > 1
        ? fhirpath.evaluate(fhirPatient, 'name.given.last()')
        : [''],
    lastName,
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    fullName: firstName + ' ' + lastName,
    dob,
    age: moment().diff(dob, 'years', false),
    gender: fhirPatient?.gender,
    genderPrefix:
      fhirPatient.gender === 'male'
        ? 'M'
        : fhirPatient.gender === 'female'
          ? 'F'
          : fhirPatient.gender === 'unknown'
            ? '?'
            : '',
    homePhone: fhirpath.evaluate(fhirPatient, "telecom.where(system='phone').where(use='home').value"),
    mobilePhone: fhirpath.evaluate(fhirPatient, "telecom.where(system='phone').where(use='mobile').value"),
    email: fhirpath.evaluate(fhirPatient, "telecom.where(system='email').value"),
    memberId: fhirpath.evaluate(
      fhirPatient,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/member-id').value"
    ),
    otherId: fhirpath.evaluate(
      fhirPatient,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/other-id').value"
    ),
    personId: fhirpath.evaluate(
      fhirPatient,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/person-id').value"
    ),
    mrnNumber: fhirpath.evaluate(
      fhirPatient,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/mrn-number').value"
    ),
    language: fhirpath.evaluate(fhirPatient, 'communication.language.text'),
    provider: providerDisplay ? [providerDisplay] : [''],
    referralPartner: getPatientExtensionCodingDisplay(fhirPatient, 'REFERRAL_PARTNER') ?? '',
    external_referral_checkbox: [
      fhirPatient?.extension?.find((e) => e?.url === 'https://projectwell.io/fhir/extensions/external-referral')
        ?.valueString,
    ],
    eligibilityGroup: fhirpath
      .evaluate(fhirPatient, "identifier.where(system='https://projectwell.io/fhir/identifiers/eligibility-group').value")
      ?.toString(),
    replaces: fhirpath.evaluate(
      fhirPatient,
      "extension.where(url='http://projectwell.io/fhir/extensions/patient-replaces').valueString"
    )?.[0],
    replacedBy: fhirpath.evaluate(
      fhirPatient,
      "extension.where(url='http://projectwell.io/fhir/extensions/patient-replaced-by').valueString"
    )?.[0],
    isInactive:
      fhirpath.evaluate(
        fhirPatient,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/member-status').value"
      )?.[0] === 'INACTIVE',
  };

  const normalizeLines = (lines: string[] | undefined): string[] =>
    (lines ?? []).filter((line) => typeof line === 'string' && line.trim() !== '');

  fhirPatient.address?.forEach((addressItem) => {
    if (addressItem.type === 'physical') {
      const physicalLines = normalizeLines(addressItem.line);
      flatPatient.delivery_addressLine1 = physicalLines?.[0] ? [physicalLines?.[0]] : [''];
      flatPatient.delivery_addressLine2 = physicalLines?.[1] ? [physicalLines?.[1]] : [''];

      flatPatient.delivery_city = fhirpath.evaluate(fhirPatient, "address.where(type='physical').city");
      flatPatient.delivery_state = fhirpath.evaluate(fhirPatient, "address.where(type='physical').state");
      flatPatient.delivery_zip = fhirpath.evaluate(fhirPatient, "address.where(type='physical').postalCode");
    } else {
      const postalLines = normalizeLines(addressItem.line);
      flatPatient.addressLine1 = postalLines?.[0] ? [postalLines?.[0]] : [''];
      flatPatient.addressLine2 = postalLines?.[1] ? [postalLines?.[1]] : [''];

      flatPatient.city = fhirpath.evaluate(fhirPatient, "address.where(type='postal').city");
      flatPatient.state = fhirpath.evaluate(fhirPatient, "address.where(type='postal').state");
      flatPatient.zip = fhirpath.evaluate(fhirPatient, "address.where(type='postal').postalCode");
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      flatPatient.address = `${flatPatient?.addressLine1} ${flatPatient?.addressLine2}. ${flatPatient?.city}, ${flatPatient?.state}. ${flatPatient?.zip} `;
    }
  });

  return flatPatient;
}

export function parseFHIRCondition(fhirCondition: Condition): FlatCondition {
  const flatCondition: FlatCondition = {
    category: fhirpath.evaluate(fhirCondition, 'Condition.category.coding.display').toString(),
    code: fhirpath
      .evaluate(fhirCondition, "Condition.code.coding.where((system = 'http://snomed.info/sct')).code")
      .toString(),
    display: fhirpath
      .evaluate(fhirCondition, "Condition.code.coding.where((system = 'http://snomed.info/sct')).display")
      .toString(),
    onSetDate: fhirpath.evaluate(fhirCondition, 'Condition.onsetPeriod.start').toString(),
    verificationStatusCode: fhirpath
      .evaluate(fhirCondition, 'Condition.verificationStatus.coding.code')
      .toString(),
    verificationStatus: fhirpath
      .evaluate(fhirCondition, 'Condition.verificationStatus.coding.display')
      .toString(),
    clinicalStatus: fhirpath.evaluate(fhirCondition, 'Condition.clinicalStatus.coding.code').toString(),
    lastModifiedBy: fhirpath
      .evaluate(
        fhirCondition,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
      )
      .toString(),
    lastUpdated: fhirpath.evaluate(fhirCondition, 'meta.lastUpdated').toString(),
  };

  return flatCondition;
}

/**
 * Flattens a medication resource for the meds tables.
 *
 * The parameter admits `MedicationRequest` as well as `Medication` because that
 * is what both callers actually pass: `components/patient/profile/meds.tsx`
 * feeds it `/MedicationRequest` search entries, and `FHIRUtils.spec.ts`
 * exercises it with one of each. Every FHIRPath below is rooted at
 * `MedicationRequest.*`, so a plain `Medication` matches none of them and
 * flattens to all-empty strings — which the spec pins.
 *
 * `Partial<...>` because callers supply search-result resources that carry only
 * the fields they were asked for; FHIR's required `intent`/`status`/`subject`
 * are never on any path evaluated here.
 */
export function parseFHIRMedication(
  fhirMedication: Partial<Medication> | Partial<MedicationRequest>
): FlatMedication {
  const flatMedication: FlatMedication = {
    validStart: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.validityPeriod.start')
      .toString(),
    validEnd: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.validityPeriod.end')
      .toString(),
    code: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.medicationCodeableConcept.coding.first().code')
      .toString(),
    display: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.medicationCodeableConcept.coding.first().display')
      .toString(),
    dosageQuantityUnit: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dosageInstruction.first().doseAndRate.first().doseQuantity.unit')
      .toString(),
    dosageQuantity: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dosageInstruction.first().doseAndRate.first().doseQuantity.value')
      .toString(),
    supplyDurationUnit: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.expectedSupplyDuration.unit')
      .toString(),
    supplyDuration: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.expectedSupplyDuration.value')
      .toString(),
    supplyQuantityUnit: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.quantity.unit')
      .toString(),
    supplyQuantity: fhirpath
      .evaluate(fhirMedication, 'MedicationRequest.dispenseRequest.quantity.value')
      .toString(),
    authoredOn: fhirpath.evaluate(fhirMedication, 'MedicationRequest.authoredOn').toString(),
    id: fhirpath.evaluate(fhirMedication, 'MedicationRequest.id').toString(),
  };

  return flatMedication;
}

export function parseFHIRLab(fhirLab: Observation): FlatLab {
  const sourceExtension = fhirLab.extension?.find(
    (extension) => extension.url === 'https://projectwell.io/fhir/identifiers/source'
  );

  const flatLab: FlatLab = {
    category: fhirpath.evaluate(fhirLab, 'Observation.category.first().coding.first().code').toString(),
    code: fhirpath.evaluate(fhirLab, 'Observation.code.coding.first().code').toString(),
    display: fhirpath.evaluate(fhirLab, 'Observation.code.coding.first().display').toString(),
    quantity: fhirpath.evaluate(fhirLab, 'Observation.valueQuantity.value').toString(),
    unit: fhirpath.evaluate(fhirLab, 'Observation.valueQuantity.unit').toString(),
    id: fhirpath.evaluate(fhirLab, 'Observation.id').toString(),
    author: fhirpath.evaluate(
      fhirLab,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
    ),
    lastUpdated: fhirpath.evaluate(fhirLab, 'Observation.meta.lastUpdated').toString(),
    effectiveDate: fhirpath.evaluate(fhirLab, 'Observation.effectivePeriod.start').toString(),
    derivedFrom: fhirpath.evaluate(fhirLab, 'Observation.derivedFrom.first().reference').toString(),
    readingType: fhirpath
      .evaluate(
        fhirLab,
        "Observation.extension.where(url='https://projectwell.io/fhir/identifiers/lab-reading-type').valueString"
      )
      .toString(),

    source: sourceExtension?.valueString?.trim() ?? '',
  };

  return flatLab;
}

export function parseFHIRreferral(fhirReferral: Observation): FlatReferral {
  const patient = fhirpath.evaluate(fhirReferral, 'subject.reference').toString();

  const rawReferralDate: unknown[] = fhirpath.evaluate(fhirReferral, 'Observation.effectivePeriod.start');

  // `moment` is handed the RAW evaluate array, as upstream does. A
  // `.filter(typeof === 'string')` here would not be inert: `fhirpath.evaluate`
  // returns whatever the server put in `effectivePeriod.start`, so a numeric start
  // yields `[20260101]` — which `moment` parses successfully but a string filter
  // drops, turning `referralAge` into `NaN`. The cast is type-only.
  const referralDateParts = rawReferralDate as string[];

  const rawReferralAge = moment(referralDateParts, 'YYYY-MM-DD hh:mm a').diff(new Date(), 'days');

  const rawLastUpdatedRaw: unknown[] = fhirpath.evaluate(fhirReferral, 'Observation.meta.lastUpdated');
  // Raw array, as upstream — see the note on `referralDateParts`: filtering to strings
  // turns a numeric `meta.lastUpdated` into "Invalid date" where upstream formatted it.
  const rawLastUpdated = rawLastUpdatedRaw as string[];

  const flatReferral: FlatReferral = {
    id: fhirpath.evaluate(fhirReferral, 'id').toString(),

    patientId: patient.indexOf('/') !== -1 ? patient.split('/')[1] : patient,

    referralType: fhirpath.evaluate(fhirReferral, 'Observation.valueString').toString(),

    referralId: fhirpath.evaluate(
      fhirReferral,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/referral-id').value"
    ),

    orgId: fhirpath.evaluate(
      fhirReferral,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/orgId').value"
    ),

    referralStatus: fhirpath.evaluate(
      fhirReferral,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/referral-status').value"
    ),

    interventions: fhirpath.evaluate(
      fhirReferral,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/interventions').value"
    ),

    lastUpdatedBy: fhirpath.evaluate(
      fhirReferral,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
    ),

    referralAge: rawReferralAge < 0 ? rawReferralAge * -1 : rawReferralAge,

    referralDate:
      rawReferralDate && moment.utc(referralDateParts, 'YYYY-MM-DDTHH:mm:ss').local().format('YYYY-MM-DD hh:mm a'),

    lastUpdatedAge: moment.utc(rawLastUpdated, 'YYYY-MM-DDTHH:mm:ss').fromNow(),

    lastUpdated:
      rawLastUpdated && moment.utc(rawLastUpdated, 'YYYY-MM-DDTHH:mm:ss').local().format('MMMM Do, YYYY HH:mm:ss'),
  };

  return flatReferral;
}

export function parseFHIRVital(fhirVital: Observation): FlatVital {
  const flatVital: FlatVital = {
    id: fhirpath.evaluate(fhirVital, 'Observation.id').toString(),
    code: fhirpath.evaluate(fhirVital, 'Observation.code.coding.first().code').toString(),
    display: fhirpath.evaluate(fhirVital, 'Observation.code.coding.first().display').toString(),
    quantity: fhirpath.evaluate(fhirVital, 'Observation.valueQuantity.value').toString(),
    unit: fhirpath.evaluate(fhirVital, 'Observation.valueQuantity.unit').toString(),
    author: fhirpath
      .evaluate(fhirVital, "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value")
      .toString(),
    effectiveDate: fhirpath.evaluate(fhirVital, 'Observation.effectivePeriod.start').toString(),
    valueCode: fhirpath.evaluate(fhirVital, 'Observation.valueCodeableConcept.coding.first().code').toString(),
    valueSystem: fhirpath
      .evaluate(fhirVital, 'Observation.valueCodeableConcept.coding.first().system')
      .toString(),
    valueString: fhirpath.evaluate(fhirVital, 'Observation.valueString').toString(),
    readingType: fhirpath
      .evaluate(
        fhirVital,
        "Observation.extension.where(url='https://projectwell.io/fhir/identifiers/lab-reading-type').valueString"
      )
      .toString(),
  };

  return flatVital;
}

export function parseFHIRPreference(fhirPreference: Observation): FlatPreference {
  // NOTE: `category` is evaluated against an empty object rather than
  // `fhirPreference`, so it has always resolved to ''. Preserved as-is: callers
  // depend on the current (empty) value.
  const emptyCategorySource = {};

  const flatPreference: FlatPreference = {
    category: fhirpath
      .evaluate(emptyCategorySource, 'Observation.category.first().coding.first().code')
      .toString(),
    code: fhirpath.evaluate(fhirPreference, 'Observation.code.coding.first().code').toString(),
    id: fhirpath.evaluate(fhirPreference, 'Observation.id').toString(),
    valueString: fhirpath.evaluate(fhirPreference, 'Observation.valueString').toString(),
    author: fhirpath.evaluate(
    fhirPreference,
    "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
  ),
    referralStatus: fhirpath
    .evaluate(
      fhirPreference,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/referral-status').value"
    )
    .toString(),
    orgId: fhirpath
    .evaluate(fhirPreference, "identifier.where(system='https://projectwell.io/fhir/orgid').value")
    ?.toString(),
    referralId: fhirpath
    .evaluate(fhirPreference, "identifier.where(system='https://projectwell.io/fhir/identifiers/referral-id').value")
    ?.toString(),
    accessMethod: fhirpath
    .evaluate(fhirPreference, "identifier.where(system='https://projectwell.io/fhir/identifiers/access-method').value")
    .toString(),
    paymentAmount: fhirpath
    .evaluate(
      fhirPreference,
      "extension.where(url='https://projectwell.io/fhir/extensions/amount-received').valueQuantity.value"
    )
    .toString(),
    failureCode: fhirpath
    .evaluate(
      fhirPreference,
      "extension.where(url='https://projectwell.io/fhir/extensions/payment-failure-code').valueString"
    )
    .toString(),
    failureMessage: fhirpath
    .evaluate(
      fhirPreference,
      "extension.where(url='https://projectwell.io/fhir/extensions/payment-failure-message').valueString"
    )
    .toString(),
    productCode: fhirpath
    .evaluate(fhirPreference, "extension.where(url='https://projectwell.io/fhir/extensions/product-id').valueString")
    .toString(),
    lastUpdated: fhirpath.evaluate(fhirPreference, 'Observation.meta.lastUpdated').toString(),
  };

  return flatPreference;
}

export function parseFHIRNotes(fhirNote: DocumentReference): FlatNote {
  const flatNote: FlatNote = {
    author: fhirpath.evaluate(
      fhirNote,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
    ),

    parentId: fhirpath.evaluate(fhirNote, 'relatesTo.first().target.reference').toString(),

    categoryCode: fhirpath.evaluate(fhirNote, 'category.first().coding.first().code').toString(),

    code: fhirpath.evaluate(fhirNote, 'type.coding.first().code').toString(),
    type: fhirpath.evaluate(fhirNote, 'type.coding.first().display').toString(),

    date: fhirpath.evaluate(fhirNote, 'date').toString(),

    description: fhirpath.evaluate(fhirNote, 'description').toString(),

    id: fhirpath.evaluate(fhirNote, 'id').toString(),

    source: fhirpath
      .evaluate(
        fhirNote,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/questionnaireResponse').value"
      )
      .toString(),

    referralId: fhirpath.evaluate(
      fhirNote,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/referral-id').value"
    ), //can be an array of multiple referral ids
  };

  return flatNote;
}

export function parseFHIRProvenance(fhirProvenance: Provenance): FlatProvenance {
  const flatProvenance: FlatProvenance = {

    id: fhirpath.evaluate(fhirProvenance, 'id').toString(),
    activityCode: fhirpath.evaluate(fhirProvenance, 'activity.coding.first().display').toString(),
    author: fhirpath.evaluate(fhirProvenance, 'agent.first().who.display').toString(),
    lastUpdated: fhirpath.evaluate(fhirProvenance, 'meta.lastUpdated').toString(),
    reason: fhirpath.evaluate(fhirProvenance, 'reason.first().coding.first().display').toString(),
    target: fhirpath.evaluate(fhirProvenance, 'target.first().reference').toString(),
  };

  return flatProvenance;
}

export function parseFHIRlocation(fhirLocation: FhirLocation): FlatLocation {
  const flatLocation: FlatLocation = {
    id: fhirpath.evaluate(fhirLocation, 'id').toString(),

    vendorName: fhirpath.evaluate(fhirLocation, 'managingOrganization.display').toString(),

    county: fhirpath.evaluate(fhirLocation, 'address.district').toString(),
    state: fhirpath.evaluate(fhirLocation, 'address.state').toString(),
    postalCode: fhirpath.evaluate(fhirLocation, 'address.postalCode').toString(),
  };

  return flatLocation;
}

/**
 * The patient's display name, or `'  '` when there is no patient.
 *
 * The parameter admits nullish because the call sites genuinely pass it: the
 * communication row components call this with `selectedCallerCard`, which is `null`
 * whenever the assign-member modal is closed, and `patient/index.tsx` calls it with a
 * `DEFAULT_CALLER` that is `null` until an inbound call resolves.
 *
 * The body never dereferences the argument — it hands it to `fhirpath.evaluate`, which
 * returns `[]` for a nullish input rather than throwing. So all three arms return the
 * concatenation of three empty arrays, i.e. the two-space string `'  '`.
 *
 * SUSPECTED DEFECT (a nameless sender is recorded as two spaces) — REPORTED, NOT FIXED.
 * `zoomRow`, `connectLogRow` and the sibling rows assign this into
 * `inboundPayload.sender.display` before PUTting the Communication, so a call assigned
 * while no member is selected records `display: '  '` on a real FHIR resource rather
 * than omitting the field or failing. Returning `undefined` for the nullish arms would
 * be the honest value but changes what is written.
 */
export function parseFHIRPatientName(fhirPatient: Patient | null | undefined): string {
  const firstName: unknown[] = fhirpath.evaluate(fhirPatient, 'name.given.first()');
  const middleName: unknown[] =
    fhirpath.evaluate(fhirPatient, 'name.given')?.length > 1
      ? fhirpath.evaluate(fhirPatient, 'name.given.last()')
      : [''];
  const lastName: unknown[] = fhirpath.evaluate(fhirPatient, 'name.family');

  // Not `.join`: `unknown[]` overstates what arrives — the same fields are read off
  // hand-built `FlatPatient` fixtures holding plain strings, and 38 specs fail with
  // `firstName.join is not a function`. `+` accepts both shapes.
  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  return firstName + ' ' + middleName + ' ' + lastName;
}

export function parseFHIRPatientEmail(fhirPatient: Patient): unknown[] {
  const email: unknown[] = fhirpath.evaluate(fhirPatient, "telecom.where(system='email').value");

  return email;
}

export function parseFHIRPatientPhone(fhirPatient: Patient): unknown[] {
  const homePhone: unknown[] = fhirpath.evaluate(fhirPatient, "telecom.where(use='home').value");

  return homePhone.length === 0 || !homePhone[0]
    ? fhirpath.evaluate(fhirPatient, "telecom.where(use='mobile').value")
    : homePhone;
}

export function parseAlertsPatients(flag: Flag): FlatAlertPatient {
  const subject: unknown[] = fhirpath.evaluate(flag, 'subject.reference');
  // Typed, not guarded. Upstream's test is `subject[0] && subject[0].indexOf('/') > 0`,
  // which THROWS when the server sends a non-string `subject.reference` (fhirpath
  // returns whatever was in the JSON). A `typeof === 'string'` guard here would turn
  // that throw into a silent `id: ''`, which downstream reads as "no patient".
  const firstSubject = subject[0] as string | undefined;

  const flatFlagRequest: FlatAlertPatient = {
    org: fhirpath.evaluate(
      flag,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/organization').value"
    )[0],

    name: fhirpath.evaluate(flag, 'author.display').toString(),

    id: firstSubject && firstSubject.indexOf('/') > 0 ? firstSubject.split('/')[1] : '',
  };

  return flatFlagRequest;
}

export function parseFHIRProvenancePatients(fhirProvenance: Provenance): string | undefined {
  const target: unknown[] = fhirpath.evaluate(
    fhirProvenance,
    "target.where(reference.substring(0,7)='Patient').reference"
  );
  const firstTarget = target[0];

  if (typeof firstTarget === 'string' && firstTarget.indexOf('/') > 0) {
    return firstTarget.split('/')[1];
  } else return '';
}

export function parseFHIRServiceRequest(fhirServiceRequest: ServiceRequest): FlatServiceRequest {
  const flatServiceReuqest: FlatServiceRequest = {

    createdBy: fhirpath
    .evaluate(
      fhirServiceRequest,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
    )
    .toString(),
    createdDate: fhirpath.evaluate(fhirServiceRequest, 'meta.lastUpdated').toString(),
    type: fhirpath.evaluate(fhirServiceRequest, 'type.coding.first().display').toString(),
    status: fhirpath.evaluate(fhirServiceRequest, 'status').toString(),
    id: fhirpath.evaluate(fhirServiceRequest, 'id').toString(),
  };

  return flatServiceReuqest;
}

export function parseFHIRCarePlan(fhirCarePlan: CarePlan): FlatCarePlan {
  const careTeamRef = fhirpath.evaluate(fhirCarePlan, 'careTeam.first().reference').toString();

  const flatCarePlan: FlatCarePlan = {
    startDate: fhirpath.evaluate(fhirCarePlan, 'period.start').toString(),
    lastModifiedBy: fhirpath
      .evaluate(fhirCarePlan, "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value")
      .toString(),
    lastUpdated: fhirpath.evaluate(fhirCarePlan, 'meta.lastUpdated').toString(),
    status: fhirpath.evaluate(fhirCarePlan, 'status').toString(),
    careTeamId: careTeamRef.substring(careTeamRef.indexOf('/') + 1),
    id: fhirpath.evaluate(fhirCarePlan, 'id').toString(),
  };

  return flatCarePlan;
}

export function parseFHIRCareTeam(fhirCareTeam: CareTeam): FlatCareTeam {
  const rawOA = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '309396002')).member.display")
    .toString();

  const rawOAID = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '309396002')).member.reference")
    .toString();

  // NOTE: the '@' test is on the display but the value taken is the *reference*.
  // Preserved as written.
  const OA = rawOA.indexOf('@') !== -1 ? rawOAID.split('@')[0] : rawOA;
  const OAID = rawOAID.indexOf('/') !== -1 ? rawOAID.split('/')[1] : rawOAID;

  const rawRD = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '159033005')).member.display")
    .toString();

  const rawRDID = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '159033005')).member.reference")
    .toString();

  const RD = rawRD.indexOf('@') !== -1 ? rawRD.split('@')[0] : rawRD;
  const RDID = rawRDID.indexOf('/') !== -1 ? rawRDID.split('/')[1] : rawRDID;

  const rawCC = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '768820003')).member.display")
    .toString();

  const rawCCID = fhirpath
    .evaluate(fhirCareTeam, "participant.where((role.coding.code = '768820003')).member.reference")
    .toString();

  const CC = rawCC.indexOf('@') !== -1 ? rawCC.split('@')[0] : rawCC;
  const CCID = rawCCID.indexOf('/') !== -1 ? rawCCID.split('/')[1] : rawCCID;

  const status =
    (RDID === undefined || RDID === '' || RDID === import.meta.env.VITE_DEFAULT_PRACTITIONER_RD_ID) &&
    (OAID === undefined || OAID === '' || OAID === import.meta.env.VITE_DEFAULT_PRACTITIONER_WA_ID)
      ? 'UN-ASSIGNED'
      : 'ASSIGNED';

  const patient = fhirpath.evaluate(fhirCareTeam, 'subject.reference').toString();

  const flatCareTeam: FlatCareTeam = {
    OA,
    OAID,
    RD,
    RDID,
    CC,
    CCID,
    status,
    id: fhirCareTeam?.id,
    patientId: patient.indexOf('/') !== -1 ? patient.split('/')[1] : patient,
  };

  return flatCareTeam;
}

export function parseFHIRFlags(fhirFlag: Flag): FlatFlag {
  const flatFlag: FlatFlag = {
    code: fhirpath.evaluate(fhirFlag, 'code.coding.first().code').toString(),

    text: fhirpath.evaluate(fhirFlag, 'code.text').toString(),

    id: fhirpath.evaluate(fhirFlag, 'id').toString(),

    lastUpdated: fhirpath.evaluate(fhirFlag, 'meta.lastUpdated').toString(),

    status: fhirpath.evaluate(fhirFlag, 'status').toString(),

    // assigned to is only applicable to Alert flags
    assignedTo: fhirpath.evaluate(
      fhirFlag,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/assigned-to').value"
    )[0],

    author: fhirpath.evaluate(fhirFlag, 'author.display').toString(),
  };

  return flatFlag;
}

export function parseFHIRAlerts(fhirFlag: Flag): FlatAlert {
  const patient = fhirpath.evaluate(fhirFlag, 'subject.reference').toString();

  const flatFlag: FlatAlert = {
    code: fhirpath.evaluate(fhirFlag, 'code.coding.first().code').toString(),

    text: fhirpath.evaluate(fhirFlag, 'code.text').toString(),

    id: fhirpath.evaluate(fhirFlag, 'id').toString(),

    lastUpdated: fhirpath.evaluate(fhirFlag, 'meta.lastUpdated').toString(),

    period: fhirpath.evaluate(fhirFlag, 'period.start').toString(),

    status: fhirpath.evaluate(fhirFlag, 'status').toString(),

    patient: patient.indexOf('/') !== -1 ? patient.split('/')[1] : patient,

    // assigned to is only applicable to Alert flags
    assignedTo: fhirpath.evaluate(
      fhirFlag,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/assigned-to').value"
    )[0],

    author: fhirpath.evaluate(fhirFlag, 'author.display').toString(),
    taskId: fhirpath.evaluate(
      fhirFlag,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/taskId').value"
    )[0],
  };

  return flatFlag;
}

export function parseCommunication(fhirCommunication: Communication): FlatCommunication {

  // NOTE: `note` is evaluated against the partially-built accumulator rather
  // than `fhirCommunication`, so it has always resolved to []. The partial holds
  // only the eight scalar fields assigned above it, none of them `note`.
  // Preserved as-is: callers depend on the current (empty) value.
  const partialForNote = {
    sender: fhirpath.evaluate(fhirCommunication, 'sender.display').toString(),
    id: fhirpath.evaluate(fhirCommunication, 'id').toString(),
    sent: fhirCommunication.sent,
    received: fhirCommunication.received,
    category: fhirpath.evaluate(fhirCommunication, 'category.first().text').toString(),
    type: fhirpath.evaluate(fhirCommunication, 'identifier.first().value').toString(),
    contactState: fhirpath
      .evaluate(
        fhirCommunication,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/communication/contact-state').value"
      )
      .toString(),
    contactId: fhirpath
      .evaluate(
        fhirCommunication,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/communication/contact-id').value"
      )
      .toString(),
  };

  const flatCommunication: FlatCommunication = {
    ...partialForNote,
    note: fhirpath.evaluate(partialForNote, 'note'),

    subject: fhirpath.evaluate(fhirCommunication, 'payload.first().contentReference.display').toString(),
    contentBody: fhirpath.evaluate(fhirCommunication, 'payload.first().contentString').toString(),
    attachmentURL: fhirpath.evaluate(fhirCommunication, 'payload.first().contentAttachment.url').toString(),
    topic: fhirpath.evaluate(fhirCommunication, 'topic.text').toString(),
    medium: readStringified(fhirpath.evaluate(fhirCommunication, 'medium.text')?.[0]),
    email: fhirpath
      .evaluate(fhirCommunication, "extension.where(url='https://projectwell.io/fhir/member-email').valueString")
      .toString(),
    emailSubject: fhirpath
      .evaluate(fhirCommunication, "extension.where(url='https://projectwell.io/fhir/email-subject').valueString")
      .toString(),
    emailBody: fhirpath
      .evaluate(fhirCommunication, "extension.where(url='https://projectwell.io/fhir/email-body').valueString")
      .toString(),
    smsNumber: fhirpath
      .evaluate(fhirCommunication, "extension.where(url='https://projectwell.io/fhir/member-sms-number').valueString")
      .toString(),
    smsBody: fhirpath
      .evaluate(fhirCommunication, "extension.where(url='https://projectwell.io/fhir/sms-message-body').valueString")
      .toString(),
    ticketId: fhirpath.evaluate(
      fhirCommunication,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/communication/ticket-id').value"
    )?.[0],
    trafficFlow: fhirpath.evaluate(
      fhirCommunication,
      "identifier.where(system='https://projectwell.io/fhir/identifiers/communication/traffic-flow').value"
    )?.[0],
    lastUpdated: fhirpath.evaluate(fhirCommunication, 'meta.lastUpdated').toString(),
  };

  return flatCommunication;
}

/**
 * Evaluates an arbitrary FHIRPath expression against a resource.
 *
 * The path is a caller-supplied string, so the element type is genuinely
 * unknowable here; `fhirpath.evaluate` always returns an array.
 */
export function evalPath(fhirResource: Resource, path: string): unknown[] {
  return fhirpath.evaluate(fhirResource, path);
}

export function parseQuestionnaireResponse(fhirQuestionnaireResponse: QuestionnaireResponse): FlatQuestionnaireResponse {
  const flatQuestionnaireResponse: FlatQuestionnaireResponse = {
    id: fhirpath.evaluate(fhirQuestionnaireResponse, 'id').toString(),
    questionnaireId: fhirpath
    .evaluate(fhirQuestionnaireResponse, 'questionnaire')
    ?.toString()
    ?.split('/')?.[1],
    status: fhirpath.evaluate(fhirQuestionnaireResponse, 'status').toString(),
    completedDate: fhirpath
    .evaluate(
      fhirQuestionnaireResponse,
      "extension.where(url='https://projectwell.io/fhir/extensions/completed-date').valueDateTime"
    )
    ?.toString(),
    lastModifiedBy: fhirpath
    .evaluate(fhirQuestionnaireResponse, 'identifier.value')
    ?.toString(),
  };

  return flatQuestionnaireResponse;
}
